import { useLocalParticipant } from '@livekit/components-react'
import { LocalVideoTrack, Track } from 'livekit-client'
import type { RefObject } from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'

interface UsePdfScreenShareOptions {
  canvasRef: RefObject<HTMLCanvasElement | null>
  frameRate?: number
}

export function usePdfScreenShare({ canvasRef, frameRate = 10 }: UsePdfScreenShareOptions) {
  const { localParticipant } = useLocalParticipant()
  const [isSharing, setIsSharing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const trackRef = useRef<LocalVideoTrack | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const streamCanvasRef = useRef<HTMLCanvasElement | null>(null)
  const animationFrameRef = useRef<number | null>(null)
  const isStartingRef = useRef(false)

  // biome-ignore lint/correctness/useExhaustiveDependencies: canvasRef is stable
  useEffect(() => {
    const sourceCanvas = canvasRef.current

    if (!sourceCanvas || !localParticipant || isSharing || isStartingRef.current) {
      return
    }

    const startSharing = async () => {
      isStartingRef.current = true

      try {
        // Wait for source canvas to have dimensions
        await new Promise<void>((resolve, reject) => {
          const timeout = setTimeout(() => reject(new Error('Timeout')), 10000)
          const check = () => {
            if (sourceCanvas.width > 0 && sourceCanvas.height > 0) {
              clearTimeout(timeout)
              resolve()
            } else {
              requestAnimationFrame(check)
            }
          }
          check()
        })

        // Create streaming canvas
        const streamCanvas = document.createElement('canvas')
        streamCanvas.width = sourceCanvas.width
        streamCanvas.height = sourceCanvas.height
        streamCanvasRef.current = streamCanvas

        const streamCtx = streamCanvas.getContext('2d')
        if (!streamCtx) throw new Error('Failed to get context')

        // Start continuous copy loop
        const copyFrame = () => {
          if (
            streamCanvas.width !== sourceCanvas.width ||
            streamCanvas.height !== sourceCanvas.height
          ) {
            streamCanvas.width = sourceCanvas.width
            streamCanvas.height = sourceCanvas.height
          }
          streamCtx.drawImage(sourceCanvas, 0, 0)
          animationFrameRef.current = requestAnimationFrame(copyFrame)
        }
        animationFrameRef.current = requestAnimationFrame(copyFrame)

        // Wait a bit for first frame to be copied
        await new Promise((r) => setTimeout(r, 100))

        // Capture and publish
        const stream = streamCanvas.captureStream(frameRate)
        streamRef.current = stream

        const videoTrack = stream.getVideoTracks()[0]
        if (!videoTrack) throw new Error('No video track')

        const localTrack = new LocalVideoTrack(videoTrack, undefined, true)
        trackRef.current = localTrack

        await localParticipant.publishTrack(localTrack, {
          source: Track.Source.ScreenShare,
          name: 'pitch-deck',
        })

        console.log(`[usePdfScreenShare] Sharing ${streamCanvas.width}x${streamCanvas.height}`)
        setIsSharing(true)
        setError(null)
      } catch (err) {
        console.error('[usePdfScreenShare] Error:', err)
        setError(err instanceof Error ? err.message : 'Failed')
        if (animationFrameRef.current) {
          cancelAnimationFrame(animationFrameRef.current)
          animationFrameRef.current = null
        }
      } finally {
        isStartingRef.current = false
      }
    }

    startSharing()
  }, [localParticipant, isSharing, frameRate])

  const stopSharing = useCallback(async () => {
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current)
      animationFrameRef.current = null
    }

    if (trackRef.current && localParticipant) {
      try {
        await localParticipant.unpublishTrack(trackRef.current)
        trackRef.current.stop()
      } catch {}
      trackRef.current = null
    }

    if (streamRef.current) {
      for (const t of streamRef.current.getTracks()) {
        t.stop()
      }
      streamRef.current = null
    }

    streamCanvasRef.current = null
    setIsSharing(false)
  }, [localParticipant])

  useEffect(() => {
    return () => {
      if (animationFrameRef.current) cancelAnimationFrame(animationFrameRef.current)
      if (trackRef.current) trackRef.current.stop()
      if (streamRef.current) {
        for (const t of streamRef.current.getTracks()) {
          t.stop()
        }
      }
    }
  }, [])

  return { isSharing, error, stopSharing }
}
